import React, { useCallback, useMemo } from 'react';
import { useSelector } from '@xstate/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import Alert from '@mui/material/Alert';
import Modal from '../components/modals/modal';
import { usePlanSelect, usePaymentDetails } from './service/plan-select-service';
import PaymentDetails from '../stripe/payment-details/payment-details';
import PaymentSummary from './payment-summary';

const planSelector = (state: any) => state.context.plan;
const currentPlanSelector = (state: any) => state.context.currentPlan;
const priceCheckSelector = (state: any) => state.context.priceCheck;
const isGettingPaymentDetailsSelector = (state: any) => state.matches('gettingPaymentDetails');
const isGettingPriceCheckSelector = (state: any) => state.matches('gettingPriceCheck');
const isConfirmingPaymentSelector = (state: any) => state.matches('confirmingPayment');
const isSubmittingPlanSelectSelector = (state: any) => state.matches('submittingPlanSelect');
const isSelectingPlanSelector = (state: any) => state.matches('selectingPlan');
const errorMessageSelector = (state: any) => state.context.errorMessage;
const minConversionLicensesSelector = (state: any) => state.context.minConversionLicenses;

const CheckoutDialog: React.FC = () => {
  const planInterpreter = usePlanSelect();
  const paymentDetailsInterpreter = usePaymentDetails();
  const plan = useSelector(planInterpreter, planSelector);
  const currentPlan = useSelector(planInterpreter, currentPlanSelector);
  const priceCheck = useSelector(planInterpreter, priceCheckSelector);
  const isGettingPaymentDetails = useSelector(planInterpreter, isGettingPaymentDetailsSelector);
  const isGettingPriceCheck = useSelector(planInterpreter, isGettingPriceCheckSelector);
  const isConfirmingPayment = useSelector(planInterpreter, isConfirmingPaymentSelector);
  const isSubmittingPlanSelect = useSelector(planInterpreter, isSubmittingPlanSelectSelector);
  const errorMessage = useSelector(planInterpreter, errorMessageSelector);
  const minConversionLicenses = useSelector(planInterpreter, minConversionLicensesSelector);
  const isSelectingPlan = useSelector(planInterpreter, isSelectingPlanSelector);

  const handleClose = useCallback(() => {
    planInterpreter.send({ type: 'CANCEL' });
  }, [planInterpreter]);

  const handlePositive = useCallback(() => {
    if (isGettingPaymentDetails) {
      paymentDetailsInterpreter.send({ type: 'NEXT' });
    }
    if (isConfirmingPayment) {
      planInterpreter.send({ type: 'CONFIRM_PAYMENT' });
    }
  }, [planInterpreter, paymentDetailsInterpreter, isGettingPaymentDetails, isConfirmingPayment]);

  const title = useMemo(() => {
    if (isGettingPaymentDetails) {
      return 'Payment Details';
    }
    if (isSubmittingPlanSelect) {
      return 'Updating Plan...';
    }
    if (!isSelectingPlan) {
      return 'Summary';
    }
    return '';
  }, [isGettingPaymentDetails, isSubmittingPlanSelect, isSelectingPlan]);

  const positiveButton = useMemo(() => {
    if (isGettingPriceCheck || isConfirmingPayment) {
      return 'Confirm';
    }
    if (isSubmittingPlanSelect) {
      return 'Submitting...';
    }
    return 'Next';
  }, [isGettingPriceCheck, isConfirmingPayment, isSubmittingPlanSelect]);

  const totalAmountDue = useMemo(() => {
    if (!priceCheck) return 'loading...';
    const invoiceIsDueNow = priceCheck.period_end < Date.now() / 1000;
    const dueNow = invoiceIsDueNow ? priceCheck.amount_due : 0;
    return `$${(dueNow / 100).toFixed(2)}`;
  }, [priceCheck]);

  const isDialogOpen = isGettingPaymentDetails
    || isGettingPriceCheck
    || isConfirmingPayment
    || isSubmittingPlanSelect;

  return (
    <Modal
      open={isDialogOpen}
      onClose={handleClose}
      title={title}
      onPositive={handlePositive}
      onNegative={handleClose}
      positive={positiveButton}
      positiveDisabled={isSubmittingPlanSelect || isGettingPriceCheck || !!errorMessage}
      negativeDisabled={isSubmittingPlanSelect}
      maxWidth="xs"
    >
      <DialogContent>
        {errorMessage && (
          <Box sx={{ marginBottom: '7px' }}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}
        {isGettingPaymentDetails && <PaymentDetails interpreter={paymentDetailsInterpreter} />}
        {(isGettingPriceCheck || isConfirmingPayment) && (
          <PaymentSummary
            selectedPlanName={plan.name}
            currentPlan={currentPlan}
            totalAmountDue={totalAmountDue}
            minConversionLicenses={minConversionLicenses}
          />
        )}
        {isSubmittingPlanSelect && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Modal>
  );
};

export default CheckoutDialog;
